import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Header } from '../../components/shared/header/header';
import { LeadsTable, LeadColumn, LeadRow, FilterConfig, ServerSidePagination } from '../../components/shared/leads-table/leads-table';
import { LeadHistoricoService } from '../../services/lead-historico.service';
import { LeadHistoricoItem, LeadHistoricoRequest } from '../../interfaces/lead-historico.interface';
import { catchError, finalize, of, Subscription } from 'rxjs';
import { StatusLeadService } from '../../services/status-lead-service.service';
import { SelectedDnService } from '../../services/selected-dn.service';
import { FilterFieldsUtil } from '../../utils/filter-fields-utils';

@Component({
  selector: 'app-leads-history',
  standalone: true,
  imports: [CommonModule, RouterModule, Header, LeadsTable],
  templateUrl: './leads-history.html',
  styleUrls: ['./leads-history.scss']
})
export class LeadsHistory implements OnInit, OnDestroy, AfterViewInit {
  private dnSubscription?: Subscription;
  private hasLoadedInitialData = false;
  private hasReceivedInitialDates = false;
  private isLoading = false;
  private initialLoadDone = false;
  private statusLeadLoaded = false;
  private defaultStatusLeadIds: number[] = [];
  private readonly originFilterEnabled = false;

  tableColumns: LeadColumn[] = [
    { key: 'nome', label: 'Nome', type: 'text' },
    { key: 'disponivelDesde', label: 'Disponibilizado em', type: 'text' },
    { key: 'tipoCampanha', label: 'Tipo Campanha', type: 'text' },
    { key: 'status', label: 'Status', type: 'status' }
  ];

  // Configuração de filtros para Leads History - SEM renewal e preAprovacao
  filterConfig: FilterConfig = {
    searchFields: ['nome'], // Busca por nome
    alphabeticalField: 'NM_NOME', // Ordenação alfabética por nome
    filterFields: {
      renewal: 'renovaMais', // Filtro renewal disponível
      preAprovado: 'preAprovacao', // Filtro pré-aprovação disponível
      origin: this.originFilterEnabled ? 'origem' : undefined // Filtro origem temporariamente desabilitado
    },
    dateField: 'disponibilizadoEm' // Filtro de data por data de disponibilização
  };

  tableData: LeadRow[] = [];
  loading: boolean = false;
  loadingMessage: string = 'Carregando dados...';
  errorMessage: string = '';
  emptyMessage: string = 'Nenhum lead encontrado para o tipo de campanha informada.';
  serverSidePagination?: ServerSidePagination;

  // Propriedades para controlar os filtros
  private currentSearchText: string | undefined = undefined;
  private currentFilters: string[] = [];
  private currentStatusLeadIdsFilters: number[] = [];
  private currentDateRange: { start: string, end: string } = { start: '', end: '' };
  private currentSortField: string = 'DT_CADASTRO';
  private currentSortOrder?: 'ASC' | 'DESC' = 'DESC';

  constructor(
    private leadHistoricoService: LeadHistoricoService,
    private statusLeadService: StatusLeadService,
    private router: Router,
    private selectedDnService: SelectedDnService
  ) { }

  ngOnInit(): void {
    // Inicializa o intervalo de datas padrão (últimos 3 meses)
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    this.currentDateRange = {
      start: this.formatDateForInput(threeMonthsAgo),
      end: this.formatDateForInput(today)
    };

    // Carrega os status de lead
    this.loadStatusLead();

    // Subscreve às mudanças do DN
    this.dnSubscription = this.selectedDnService.selectedDn$.subscribe(dn => {
      if (dn && this.hasReceivedInitialDates && this.statusLeadLoaded && !this.initialLoadDone) {
        this.loadData(1, this.serverSidePagination?.pageSize || 20);
      }
    });
  }

  ngAfterViewInit(): void {
    // Aguarda para garantir que os filtros de status foram carregados antes de fazer a primeira requisição
    setTimeout(() => {
      if (!this.hasLoadedInitialData && !this.hasReceivedInitialDates && !this.isLoading) {
        this.hasReceivedInitialDates = true;
        if (this.statusLeadLoaded) {
          // Status já carregado (cache), pode carregar os dados imediatamente
          this.loadData();
        }
        // Se status ainda não carregou, o handler de sucesso de loadStatusLead() chamará loadData()
      }
    }, 200);
  }

  ngOnDestroy(): void {
    // Limpa a subscription para evitar memory leaks
    if (this.dnSubscription) {
      this.dnSubscription.unsubscribe();
    }
  }

  loadStatusLead() {
    this.statusLeadService.getAllList().subscribe({
      next: (response) => {
        // Calcula os IDs padrão: todos os "Venda Perdida" e "Venda Efetivada"
        this.defaultStatusLeadIds = response
          .filter(s =>
            s.nome.toLowerCase().includes('venda perdida') ||
            s.nome.toLowerCase() === 'venda efetivada'
          )
          .map(s => s.id);

        this.currentStatusLeadIdsFilters = [...this.defaultStatusLeadIds];
        this.statusLeadLoaded = true;

        this.filterConfig = {
          searchFields: ['nome'],
          alphabeticalField: 'NM_NOME',
          filterFields: {
            renewal: 'renovaMais',
            preAprovado: 'preAprovacao',
            origin: this.originFilterEnabled ? 'origem' : undefined,
            statusLead: response,
            selectedStatusLeadIds: [...this.defaultStatusLeadIds]
          },
          dateField: 'disponibilizadoEm'
        };

        // Se ainda não carregamos os dados iniciais, carregamos agora
        if (!this.hasLoadedInitialData && this.hasReceivedInitialDates) {
          this.loadData();
        }
      },
      error: (error) => {
        console.error('Erro ao carregar status lead:', error);
        this.statusLeadLoaded = true;
        // Mesmo se der erro, tenta carregar os dados
        if (!this.hasLoadedInitialData && this.hasReceivedInitialDates) {
          this.loadData();
        }
      }
    });
  }

  loadData(
    page: number = 1,
    pageSize: number = 20
  ): void {
    if (!this.statusLeadLoaded) {
      return;
    }

    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.loading = true;
    this.loadingMessage = 'Carregando leads...';
    this.errorMessage = '';
    this.emptyMessage = 'Nenhum lead encontrado para o tipo de campanha informada.';

    const request: LeadHistoricoRequest = {
      dn: this.selectedDnService.getSelectedDnAsNumber(),
      leadStatusIds: this.currentStatusLeadIdsFilters,
      preAprovado: FilterFieldsUtil.getPreAprovadoFromFilters(this.currentFilters),
      renovaMais: FilterFieldsUtil.getRenovaMaisFromFilters(this.currentFilters),
      origem: FilterFieldsUtil.getOrigemFromFilters(this.currentFilters),
      nome: this.currentSearchText,
      dataInicio: this.parseDate(this.currentDateRange.start),
      dataFim: this.parseDate(this.currentDateRange.end),
      page: page,
      pageSize: pageSize,
      sortField: this.currentSortField,
      sortOrder: this.currentSortOrder
    };

    this.leadHistoricoService.getLeadHistorico(request).pipe(
      catchError((error) => {
        this.isLoading = false;
        this.loading = false;

        if (error.status === 401) {
          this.errorMessage = 'Não autorizado. Redirecionando...';
          setTimeout(() => {
            this.router.navigate(['/login']);
          }, 2000);
        } else if (error.status === 204) {
          this.emptyMessage = 'Nenhum lead encontrado para o tipo de campanha informada.';
          this.tableData = [];
          this.hasLoadedInitialData = true;
          this.initialLoadDone = true;
        } else {
          this.errorMessage = 'Erro ao carregar dados. Tente novamente.';
        }

        return of(null);
      }),
      finalize(() => {
        this.isLoading = false;
        this.loading = false;
      })
    ).subscribe((response) => {
      if (response && response.data) {
        const mappedData = this.mapApiDataToTableData(response.data);
        this.tableData = [...mappedData];
        this.serverSidePagination = {
          totalRecords: response.pagination.totalRecords,
          page: response.pagination.page,
          pageSize: response.pagination.pageSize,
          from: response.pagination.from,
          to: response.pagination.to
        };
        this.hasLoadedInitialData = true;
        this.initialLoadDone = true;
      } else {
        this.emptyMessage = response?.message || 'Nenhum lead encontrado para o tipo de campanha informada.';
        this.tableData = [];
        this.hasLoadedInitialData = true;
        this.initialLoadDone = true;
      }
    });
  }

  onDateRangeChange(dateRange: { start: string, end: string }): void {
    if (this.currentDateRange.start === dateRange.start &&
      this.currentDateRange.end === dateRange.end) {
      return;
    }

    this.hasReceivedInitialDates = true;

    if (dateRange.start && dateRange.end) {
      this.currentDateRange = dateRange;
    } else {
      this.currentDateRange = dateRange;
    }

    this.loadData();
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private mapApiDataToTableData(apiData: LeadHistoricoItem[]): LeadRow[] {
    return apiData.map((item) => {
      return {
        idLead: item.idLead,
        nome: item.nomeCliente || '-',
        tipoCampanha: item.nmTipoCampanha || item.tipoCampanha || '-',
        disponivelDesde: (item.disponibilizadoEm && item.disponibilizadoEm !== 'null' && item.disponibilizadoEm !== 'undefined') ? item.disponibilizadoEm : '-',
        status: item.statusLead || '-',
        statusId: item.statusId || null,
        renewal: this.mapRenovaMais(item),
        preAprovacao: this.mapPreAprovado(item)
      };
    });
  }

  private mapPreAprovado(item: LeadHistoricoItem): string {
    if (this.isRebuyingCampaign(item)) {
      return 'Não Solicitado';
    }

    if (item.preAprovado === 'S') {
      return 'Sim';
    } else if (item.preAprovado === 'N') {
      return 'Necessita Análise';
    } else {
      return 'Não Solicitado';
    }
  }

  private isRebuyingCampaign(item: LeadHistoricoItem): boolean {
    const tipoCampanhaId = this.getTipoCampanhaId(item);
    return tipoCampanhaId === 3;
  }

  private getTipoCampanhaId(item: LeadHistoricoItem): number | null {
    const rawTipoCampanhaId =
      item.tipoCampanhaId
      ?? item['idTipoCampanha']
      ?? item['Id_Tipo_campanha']
      ?? item['id_tipo_campanha']
      ?? item['ID_TIPO_CAMPANHA'];

    if (rawTipoCampanhaId === null || rawTipoCampanhaId === undefined || rawTipoCampanhaId === '') {
      return null;
    }

    const parsed = Number(rawTipoCampanhaId);
    return Number.isNaN(parsed) ? null : parsed;
  }

  private mapRenovaMais(item: LeadHistoricoItem): string {
    return item.renovaMais === true ? 'renewalBlue' : 'renewalGrey';
  }

  // Métodos para ordenação server-side
  onSortChange(sort: { field: string, order: 'ASC' | 'DESC' }): void {
    this.currentSortField = sort.field;
    this.currentSortOrder = sort.order;
    this.loadData(1, this.serverSidePagination?.pageSize || 20);
  }

  // Métodos para filtros client-side
  onSearchChange(searchText: string): void {
    this.currentSearchText = searchText;
    this.loadData();
  }

  onFilterChange(filters: string[]): void {
    this.currentFilters = filters;
    this.loadData();
  }

  onFilterStatusLeadChange(filters: number[]): void {
    this.currentStatusLeadIdsFilters = filters;
    this.loadData();
  }

  onClearAllFilters(): void {
    this.currentSearchText = '';
    this.currentFilters = [];
    this.currentStatusLeadIdsFilters = [...this.defaultStatusLeadIds]; // Restaura os IDs padrão (Venda Perdida + Venda Efetivada)
    this.currentDateRange = { start: '', end: '' };
    this.currentSortField = 'DT_CADASTRO';
    this.currentSortOrder = 'DESC';

    // Restaura o intervalo de datas padrão
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);

    this.currentDateRange = {
      start: this.formatDateForInput(threeMonthsAgo),
      end: this.formatDateForInput(today)
    };

    this.hasReceivedInitialDates = true;

    // Atualiza o filterConfig para que os checkboxes de status sejam restaurados visualmente
    this.updateFilterConfigSelectedStatusIds(this.defaultStatusLeadIds);

    // Restaura os dados originais
    this.loadData();
  }

  private updateFilterConfigSelectedStatusIds(ids: number[]): void {
    if (this.filterConfig.filterFields) {
      // Nova referência de objeto para forçar detecção de mudança no filho
      this.filterConfig = {
        ...this.filterConfig,
        filterFields: {
          ...this.filterConfig.filterFields,
          selectedStatusLeadIds: [...ids]
        }
      };
    }
  }

  onPageSizeChange(newPageSize: number): void {
    this.loadData(1, newPageSize);
  }

  onPageChange(newPage: number): void {
    if (this.serverSidePagination) {
      this.loadData(newPage, this.serverSidePagination.pageSize);
    }
  }

  private parseDate(dateString: string): Date | undefined {
    if (!dateString || dateString.trim() === '') {
      return undefined;
    }

    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    if (dateString.includes('-')) {
      return new Date(dateString);
    }
    return undefined;
  }

  onViewLead(lead: LeadRow): void {
    if (lead.idLead) {
      this.router.navigate(['/leads/leads-info'], {
        queryParams: { idLead: lead.idLead }
      });
    }
  }
}
